/**
 * authFetch — Authenticated fetch wrapper.
 *
 * Responsibilities:
 *   1. Inject Supabase JWT into `Authorization` header.
 *   2. Stamp a UUID v4 on `X-Request-Id` so server logs/traces can be
 *      correlated with the originating UI action. We generate a fresh id per
 *      request rather than per-session so each call is individually traceable.
 *   3. Ensure `Content-Type: application/json` is set for string bodies.
 */
import { supabase } from "./supabase";
import { setCurrentRequestId } from "../sentry";
import config from "../config";

/**
 * Generate a UUID v4 using `crypto.randomUUID` when available (all modern
 * browsers + Node 20+ / Electron 30+). Falls back to a timestamp+random
 * composite for exotic runtimes — good enough for a request correlation id,
 * not used for security.
 */
function newRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;

  const headers = new Headers(options.headers || {});

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // Propagate a request-id header. If the caller already set one (e.g. a
  // retry path that wants to preserve the original id) keep it.
  if (!headers.has("X-Request-Id")) {
    headers.set("X-Request-Id", newRequestId());
  }

  // Stamp the same id on the Sentry scope so any error captured between
  // now and the next fetch carries it as `request_id`. Server-side logs
  // and Sentry events share this value, enabling a one-click pivot from
  // Sentry to the matching log stream in Loki.
  setCurrentRequestId(headers.get("X-Request-Id") ?? undefined);

  // Ensure Content-Type is set for JSON bodies
  if (options.body && typeof options.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Resolve relative URLs against the backend API URL if configured
  let finalUrl = url;
  if (url.startsWith("/") && config.API_URL) {
    // Prevent double slashes
    const base = config.API_URL.endsWith("/") ? config.API_URL.slice(0, -1) : config.API_URL;
    finalUrl = `${base}${url}`;
  }

  return fetch(finalUrl, {
    ...options,
    headers,
  });
}
